import React, { useEffect, useState } from "react";
import { useGlobalState } from "../helpers/GlobalStateProvider";
import TosAgreementModal from "./TosAgreementModal";

const TosAgreementWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { info, refreshAccountInfo } = useGlobalState();
  const [showTosModal, setShowTosModal] = useState(false);

  // Determine if TOS still needs agreement
  const tosNeedsAgreement = !info?.tosAgreed;

  // Key for storing the last shown timestamp in localStorage
  const TOS_MODAL_LAST_SHOWN_KEY = 'tosModalLastShown';
  const TOS_MODAL_DELAY_MINUTES = 10;

  useEffect(() => {
    if (tosNeedsAgreement && info) {
      const now = Date.now();
      const lastShown = localStorage.getItem(TOS_MODAL_LAST_SHOWN_KEY);
      
      if (!lastShown) {
        // First time showing the modal
        setShowTosModal(true);
        localStorage.setItem(TOS_MODAL_LAST_SHOWN_KEY, now.toString());
      } else {
        const lastShownTime = parseInt(lastShown);
        const timeSinceLastShown = now - lastShownTime;
        const delayInMs = TOS_MODAL_DELAY_MINUTES * 60 * 1000; // 10 minutes in milliseconds
        
        if (timeSinceLastShown >= delayInMs) {
          // 10 minutes have passed, show the modal again
          setShowTosModal(true);
          localStorage.setItem(TOS_MODAL_LAST_SHOWN_KEY, now.toString());
        }
      }
    }
  }, [tosNeedsAgreement, info]);

  const handleTosClose = async () => {
    setShowTosModal(false);
    // Refresh user info to get updated tosAgreed status
    const updatedInfo = await refreshAccountInfo();
    
    // Use the fresh data from refreshAccountInfo instead of stale info state
    // If they still haven't agreed after refreshing, update the timestamp
    // so the modal doesn't appear again for another 10 minutes
    if (!updatedInfo?.tosAgreed) {
      localStorage.setItem(TOS_MODAL_LAST_SHOWN_KEY, Date.now().toString());
    } else {
      // If they have agreed, clear the timestamp as it's no longer needed
      localStorage.removeItem(TOS_MODAL_LAST_SHOWN_KEY);
    }
  };

  return (
    <>
      {children}
      {showTosModal && <TosAgreementModal onClose={handleTosClose} />}
    </>
  );
};

export default TosAgreementWrapper;